import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import type { RootState } from '#shared';

export type NotificationSeverity = 'success' | 'info' | 'warning' | 'error';

export interface NotificationOptions {
  message: string;
  severity?: NotificationSeverity;
  autoHideDuration?: number;
  action?: string; // Action identifier if needed
}

export interface Notification extends NotificationOptions {
  id: string;
  open: boolean;
  severity: NotificationSeverity;
  autoHideDuration: number;
}

interface NotificationState {
  notifications: Notification[];
}

const initialState: NotificationState = {
  notifications: [],
};

export const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    showNotification: (state, action: PayloadAction<NotificationOptions>) => {
      const id = `notification-${Date.now()}-${Math.random()}`;
      const notification: Notification = {
        id,
        open: true,
        severity: action.payload.severity || 'info',
        autoHideDuration: action.payload.autoHideDuration || 6000,
        message: action.payload.message,
        action: action.payload.action,
      };
      state.notifications.push(notification);
    },
    hideNotification: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(
        n => n.id === action.payload,
      );
      if (notification) {
        notification.open = false;
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        n => n.id !== action.payload,
      );
    },
    clearAllNotifications: state => {
      state.notifications = [];
    },
  },
});

export const notificationActions = notificationSlice.actions;

// Selectors
export const notificationSelectors = {
  useSelectNotifications: () =>
    useSelector((state: RootState) => state.notification.notifications),
  useSelectHasNotifications: () =>
    useSelector(
      (state: RootState) => state.notification.notifications.length > 0,
    ),
};
